// components/CrewSection.jsx - Responsive crew signatures with Arborist in Charge
import React, { useState, useRef, useEffect, useCallback } from 'react';

const CrewSection = ({ data, onChange }) => {
  const crew = data || [];

  const updateCrew = (newCrew) => {
    onChange(newCrew);
  };

  const addCrewMember = () => {
    updateCrew([...crew, { name: '', signature: null, signedAt: null }]);
  };

  const updateCrewMember = (index, updates) => {
    const newCrew = [...crew];
    newCrew[index] = { ...newCrew[index], ...updates };
    updateCrew(newCrew);
  };

  const removeCrewMember = (index) => {
    updateCrew(crew.filter((_, i) => i !== index));
  };

  // Get crew member title based on index
  const getCrewMemberTitle = (index) => {
    if (index === 0) return 'Arborist in Charge';
    return `Crew Member ${index + 1}`;
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <h2 style={{ 
        fontSize: '18px', 
        fontWeight: '600', 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        👥 Crew Signatures
      </h2>

      <p style={{ 
        fontSize: '14px', 
        color: '#5D757E', 
        marginBottom: '16px' 
      }}>
        Each crew member must sign to acknowledge they have reviewed the safety assessment.
      </p>

      {/* Crew Members */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {crew.map((member, index) => (
          <CrewMemberCard
            key={index}
            index={index}
            member={member}
            title={getCrewMemberTitle(index)}
            onUpdate={(updates) => updateCrewMember(index, updates)}
            onRemove={() => removeCrewMember(index)}
            canRemove={crew.length > 1}
          />
        ))}
      </div>

      {/* Add Crew Member Button */}
      <button
        onClick={addCrewMember}
        style={{
          marginTop: '16px',
          padding: '12px 20px',
          backgroundColor: '#F1F0E9',
          border: '2px dashed #DADFE2',
          borderRadius: '8px',
          cursor: 'pointer',
          width: '100%',
          fontSize: '14px',
          color: '#5D757E',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <span style={{ fontSize: '20px' }}>+</span>
        Add Crew Member
      </button>

      {/* Summary */}
      {crew.length > 0 && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: crew.every(m => m.signature) ? '#E7F0E5' : '#ffffff',
          borderLeft: `4px solid ${crew.every(m => m.signature) ? '#388523' : '#D24232'}`,
          border: '1px solid #DADFE2',
          borderLeftWidth: '4px',
          borderLeftColor: crew.every(m => m.signature) ? '#388523' : '#D24232',
          borderRadius: '0 8px 8px 0',
          textAlign: 'center',
          fontSize: '14px',
          color: '#032B3A'
        }}>
          {crew.filter(m => m.signature).length} of {crew.length} crew members signed
        </div>
      )}
    </div>
  );
};

// Individual crew member card with signature
const CrewMemberCard = ({ index, member, title, onUpdate, onRemove, canRemove }) => {
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const isSigned = !!member.signature;
  const hasName = !!member.name?.trim();

  return (
    <div style={{
      backgroundColor: isSigned ? '#E7F0E5' : '#ffffff',
      borderLeft: `4px solid ${isSigned ? '#388523' : (hasName ? '#D24232' : '#DADFE2')}`,
      borderRadius: '0 8px 8px 0',
      padding: '16px',
      border: '1px solid #DADFE2',
      borderLeftWidth: '4px',
      borderLeftColor: isSigned ? '#388523' : (hasName ? '#D24232' : '#DADFE2')
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#032B3A' }}>
          {title}
          {index === 0 && !isSigned && <span style={{ color: '#D24232', marginLeft: '4px' }}>*</span>}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isSigned ? (
            <span style={{ fontSize: '13px', color: '#388523', fontWeight: '600' }}>
              ✓ Signed
            </span>
          ) : hasName ? (
            <span style={{ fontSize: '13px', color: '#D24232' }}>
              Signature required
            </span>
          ) : null}
          {canRemove && (
            <button
              onClick={onRemove}
              style={{
                padding: '4px 8px',
                backgroundColor: '#FAECEA',
                color: '#D24232',
                border: '1px solid #D24232',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {/* Name Input */}
      <input
        type="text"
        value={member.name || ''}
        onChange={(e) => onUpdate({ name: e.target.value })}
        placeholder="Enter name"
        style={{
          width: '100%',
          padding: '12px',
          border: '1px solid #DADFE2',
          borderRadius: '6px',
          fontSize: '15px',
          marginBottom: '12px',
          boxSizing: 'border-box',
          backgroundColor: '#ffffff'
        }}
      />

      {/* Signature Area */}
      {member.signature ? (
        <div>
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #DADFE2',
            borderRadius: '6px',
            padding: '8px',
            marginBottom: '8px'
          }}>
            <img
              src={member.signature}
              alt="Signature"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '100px',
                objectFit: 'contain'
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: '#5D757E'
          }}>
            <span>Signed: {new Date(member.signedAt).toLocaleString()}</span>
            <button
              onClick={() => {
                onUpdate({ signature: null, signedAt: null });
                setShowSignaturePad(true);
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: 'transparent',
                color: '#5D757E',
                border: '1px solid #DADFE2',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Re-sign
            </button>
          </div>
        </div>
      ) : (
        <div>
          {showSignaturePad ? (
            <SignaturePad
              onSave={(signature) => {
                onUpdate({ signature, signedAt: new Date().toISOString() });
                setShowSignaturePad(false);
              }}
              onCancel={() => setShowSignaturePad(false)}
            />
          ) : (
            <button
              onClick={() => setShowSignaturePad(true)}
              disabled={!member.name?.trim()}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: member.name?.trim() ? '#F1F0E9' : '#DADFE2',
                color: member.name?.trim() ? '#233D48' : '#9ca3af',
                border: '1px solid #DADFE2',
                borderRadius: '6px',
                cursor: member.name?.trim() ? 'pointer' : 'not-allowed',
                fontSize: '15px',
                fontWeight: '500'
              }}
            >
              ✍️ Tap to Sign
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Responsive signature pad component
const SignaturePad = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 150 });

  // Resize canvas to fit container
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const width = Math.min(containerWidth - 4, 600); // Max 600px, account for border
        const height = Math.min(Math.round(width * 0.4), 200); // 40% aspect ratio, max 200px
        setCanvasSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Set up canvas context when size changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [canvasSize]);

  // Get position from event (handles both mouse and touch)
  const getPosition = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle touch events
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    
    // Handle mouse events
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);
    
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  }, [getPosition]);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPosition(e);
    
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  }, [isDrawing, getPosition]);

  const stopDrawing = useCallback((e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const save = () => {
    if (!hasSignature) return;
    
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <div style={{
        border: '2px solid #DADFE2',
        borderRadius: '6px',
        backgroundColor: 'white',
        marginBottom: '12px',
        overflow: 'hidden'
      }}>
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            touchAction: 'none',
            cursor: 'crosshair'
          }}
        />
      </div>
      
      <p style={{ 
        fontSize: '12px', 
        color: '#5D757E', 
        textAlign: 'center',
        marginBottom: '12px'
      }}>
        Sign above using your finger or mouse
      </p>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#F1F0E9',
            border: '1px solid #DADFE2',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Cancel
        </button>
        <button
          onClick={clear}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#F5F0D1',
            border: '1px solid #CDB52D',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Clear
        </button>
        <button
          onClick={save}
          disabled={!hasSignature}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: hasSignature ? '#388523' : '#DADFE2',
            color: hasSignature ? 'white' : '#9ca3af',
            border: 'none',
            borderRadius: '6px',
            cursor: hasSignature ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default CrewSection;